import React, { useState } from "react";
import { MediaQueriesContext, AuthContext } from "@/App.jsx";
import { useHistory } from "react-router-dom";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import useQueryParams from "Utils/useQueryParams";
import PaginationControls from "./components/paginationControls/PaginationControls";
import usePaginatedList from "Utils/usePaginatedList";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import TranslatedString from "Components/TranslatedString";
import _ from "lodash";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import Button from "Components/button/Button";
import t from "Utils/translation";
import { plus, addSuppressionList, addManager } from "Icons/index";
import DropdownMenu from "react-dd-menu";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import { Icon, more, enable, disable, delivered, trash, refresh } from "Icons/index.js";
import s from "../delivery.scss";
import cx from "classnames";
import useToggle from "Utils/useToggle";
import QboxmailApi from "../../../qboxmailApi";
import { encodeEmailUrl } from "Utils/strings";
import Tooltip from "Components/tooltip/Tooltip";

const api = new QboxmailApi({ useCookie: true });

const SuppressionListRow = ({
  active = null,
  email,
  reason,
  dateAdded,
  numberOfAccounts,
  isStatusUpdating = false,
  customerCode = null,
  refreshList,
  onDeleteStart,
  onDeleteEnd,
  isLoggedUserAdmin,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { isToggled: isMenuOpen, toggle: toggleMenu, setIsToggled: setIsMenuOpen } = useToggle();
  const {
    isToggled: isConfirmationOpened,
    toggle: toggleConfirmation,
    setIsToggled: setIsConfirmationOpened,
  } = useToggle();

  const { isMediaMobile } = useContext(MediaQueriesContext);
  const history = useHistory();

  const deleteItem = async () => {
    setIsDeleting(true);
    try {
      await api.deleteSuppressionListItem({ email, customer_code: customerCode });
      await refreshList();
    } catch (error) {
      console.error(error);
    }
    setIsDeleting(false);
    setIsConfirmationOpened(false);
  };

  const changeStatus = async (email, customerCode, active) => {
    try {
      setIsEditing(true);
      //eslint-disable-next-line
      const done = await api.updateSuppressionListItem({ email, customer_code: customerCode, active });
      await refreshList(true);
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  const goToDetails = () => {
    if (isLoggedUserAdmin) {
      history.push(`/customers/${customerCode}/delivery/suppression-list/${encodeEmailUrl(email)}`, { fromList: true });
    } else {
      history.push(`/delivery/suppression-list/${encodeEmailUrl(email)}`, { fromList: true });
    }
  };

  return (
    <>
      {isConfirmationOpened && (
        <ConfirmModal
          title={t("confirmationModal.title.delete.suppressionList")}
          text={<TranslatedString subs={{ nameAdmin: email }}>confirmationModal.text.delete.admin</TranslatedString>}
          textButton={`button.delete`}
          opened={isConfirmationOpened}
          action={deleteItem}
          isLoading={isDeleting}
          handleClose={() => setIsConfirmationOpened(false)}
        />
      )}

      <tr>
        <td>
          <div className="flex items-center gap-1">
            <Tooltip text={t("suppressionList.enabledDelivery")}>
              <div className="flex items-center justify-center w-6">
                {isEditing ? (
                  <Icon className="syncIcon" svg={refresh} width={".8rem"} height={".8rem"} />
                ) : active ? null : (
                  <Icon svg={delivered} />
                )}
              </div>
            </Tooltip>

            <a onClick={goToDetails}>{email}</a>
          </div>
        </td>

        <td>{reason}</td>
        {!isMediaMobile && <td>{new Date(dateAdded).toLocaleDateString()}</td>}
        {!isMediaMobile && <td>{numberOfAccounts}</td>}
        <td>
          <DropdownMenu
            close={() => setIsMenuOpen(false)}
            animate={false}
            isOpen={isMenuOpen}
            toggle={
              <div style={isStatusUpdating ? { visibility: "hidden" } : {}}>
                <ButtonIcon
                  className={s.ddMenu}
                  icon={more}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu();
                  }}
                />
              </div>
            }
            align="right"
          >
            <li>
              <a
                onClick={(e) => {
                  e.stopPropagation();
                  changeStatus(email, customerCode, !active);
                  setIsMenuOpen(false);
                }}
              >
                <Icon svg={active ? delivered : disable} />
                <div>{active ? t("ddMenu.suppressionListDeliver") : t("ddMenu.suppressionListDontDeliver")}</div>
              </a>
            </li>

            <li>
              <a onClick={(e) => setIsConfirmationOpened(true)}>
                <Icon svg={trash} />
                <div>{t("ddMenu.delete")}</div>
              </a>
            </li>

            {isMediaMobile && (
              <li className="cancelBtn">
                <a>
                  <div>{t("ddMenu.cancel")}</div>
                </a>
              </li>
            )}
          </DropdownMenu>
        </td>
      </tr>
    </>
  );
};

const List = () => {
  const history = useHistory();
  const media = useContext(MediaQueriesContext);
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const location = useLocation();
  const searchParams = useQueryParams();

  const { data, isRefreshing, refreshList, isFiltering, error } = usePaginatedList(
    `/delivery/suppression_lists?${searchParams}`
  );

  const { resources: items, pagination } = data || {};

  const addNew = () => {
    history.push("/delivery/suppression-list/new");
  };

  const customerCode = location.query.customer_code;

  const isAdminAndCustomerNotSelected = () => {
    return isLoggedUserAdmin() && !customerCode;
  };

  const isListLoading = () => {
    if (isAdminAndCustomerNotSelected()) {
      return false;
    }

    return !data || isRefreshing || isFiltering || error;
  };

  return (
    <div className="screenContainer">
      <div className={s.listHeader}>
        <div>
          <div className="sectionTitle">{t("delivery.suppressionList.list.title")}</div>

          {!media.isMediaMobile && (
            <div className="sectionDesc marginRight80">{t("delivery.suppressionList.list.desc")}</div>
          )}

          <Button
            docsKey="deliverySuppressionList"
            color="docs"
            className={cx("mt-3", s.btnList)}
            onlyIcon={media.isMediaMobile}
          >
            button.docs
          </Button>
        </div>

        <div className={s.actionsList}>
          <Button
            // icon={imports}
            onClick={() => history.push("/delivery/suppression-list/import")}
            color="outline"
            className={s.btnList}
            onlyIcon={media.isMediaMobile}
          >
            button.import.list
          </Button>
          <Button icon={plus} onClick={addNew} onlyIcon={media.isMediaMobile}>
            button.add.address
          </Button>
        </div>
      </div>

      <PaginationControls
        paginationInfo={pagination}
        onlyCustomerSelectActive={isLoggedUserAdmin() && !customerCode}
        refreshList={refreshList}
        isLoading={isRefreshing}
        statusLabelPrefix="suppressionList"
        forceShowStatusFilter
      />
      <div className={s.contentList}>
        <div className={s.listContainer}>
          <ResponsiveTable
            iconPadding
            history={history}
            areRowsLoading={isListLoading()}
            location={location}
            addIcon={isAdminAndCustomerNotSelected() ? addManager : addSuppressionList}
            addMsg={
              isAdminAndCustomerNotSelected()
                ? t("table.row.msg.selectCustomer")
                : t("table.row.add.msg.suppressionList")
            }
            addDesc={
              isAdminAndCustomerNotSelected()
                ? t("table.row.desc.selectCustomer")
                : t("table.row.add.desc.suppressionList")
            }
            headers={_.compact([
              {
                name: t("table.header.email"),
                key: "email",
                sortable: true,
                activeSorting: searchParams.get("sort_field") === "email" && searchParams.get("sort"),
              },

              {
                name: t("table.header.reason"),
              },
              {
                name: t("table.header.date.added"),
                hideOnMobile: true,
                sortable: true,
                activeSorting: searchParams.get("sort_field") === "created_at" && searchParams.get("sort"),
                key: "created_at",
              },

              {
                name: t("table.header.delivery.suppressionList.account"),
                hideOnMobile: true,
              },

              { size: "3rem", center: true },
            ])}
            rows={_.map(items, (item) => {
              return (
                <SuppressionListRow
                  key={item.email}
                  isLoggedUserAdmin={isLoggedUserAdmin()}
                  email={item.email}
                  active={item.active}
                  reason={item.reason}
                  dateAdded={item.created_at}
                  numberOfAccounts={item.accounts ? item.accounts.length : t("delivery.suppressionList.list.all")}
                  customerCode={customerCode}
                  refreshList={refreshList}
                />
              );
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default List;
